import type {
  BusinessGetResponseType,
  BusinessUpdateRequestType,
  BusinessUpdateResponseType,
} from "@sellar-io/api-v2/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useFirebaseUser } from "../../../useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../useSellarCurrentBusiness.shared";
import { apiV2AssertResponse, apiV2Request } from "../apiV2Request";
import { ServerStateKeys } from "../ServerStateKeys";

export const useBusinessUpdate = (businessId?: number) => {
  const queryClient = useQueryClient();
  const { business } = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();

  return useMutation({
    mutationFn: (data: BusinessUpdateRequestType) =>
      apiV2Request({
        user,
        business,
        method: "put",
        path: `businesses/${businessId}`,
        data,
      }).then(
        (response) =>
          apiV2AssertResponse(response).data as BusinessUpdateResponseType,
      ),
    onSettled: (response) => {
      queryClient.invalidateQueries({
        queryKey: [
          user?.uid,
          business?.id,
          ServerStateKeys.Businesses,
          businessId,
        ],
      });
      // This is used on app initialisation
      // & was causing issues when updating email in checkout
      queryClient.setQueriesData<BusinessGetResponseType>(
        {
          predicate: ({ queryKey }) => {
            return (
              queryKey[0] === user?.uid &&
              queryKey[1] === business?.id &&
              queryKey[2] === ServerStateKeys.Businesses &&
              typeof queryKey[3] === "object"
            );
          },
        },
        (data) => {
          if (!data || !response) return;
          return {
            ...data,
            email: response.email,
          };
        },
      );
      queryClient.invalidateQueries({
        queryKey: [business?.id, ServerStateKeys.OrdersLegacy],
      });
      queryClient.invalidateQueries({
        queryKey: [user?.uid, business?.id, ServerStateKeys.OrdersLegacy],
      });
      queryClient.invalidateQueries({
        queryKey: [user?.uid, business?.id, ServerStateKeys.Orders],
      });
      queryClient.invalidateQueries({
        queryKey: [user?.uid, business?.id, ServerStateKeys.Businesses],
      });
      queryClient.invalidateQueries({
        queryKey: [user?.uid, business?.id, ServerStateKeys.BuyerRelationship],
      });
      queryClient.invalidateQueries({
        queryKey: [
          user?.uid,
          business?.id,
          ServerStateKeys.DeliveryAreaSellarDistribution,
        ],
      });
    },
  });
};
